
'use strict';

var $ = require('jquery'),
  Backbone = require('backbone'),
  _ = require('underscore'),
  config = require('../js/config'),
  utilities = require('../js/utilities'),
  GenericController = require('../js/GenericController'),
  ServerErrorResponseModel = require('../js/ServerErrorResponseModel'),
  ProjectModel = require('../components/DesignCenter/projectModel'),
  events = require('../js/config').events,
  ProjectsCollection = require('../components/DesignCenter/projectsCollection');

let projectsService;

export class ProjectsService extends GenericController {

  static getInstance() {
    if (!projectsService) {
      projectsService = new ProjectsService({
        collection: new ProjectsCollection()
      });
    }
    return projectsService;
  }

  // collection: should be a projects collection
  initialize() {
    this.documentUriProjectsCollections = [];
    this.productProjectsCollections = [];
    this.model = new Backbone.Model({
      creatingProjects: [],
      deletingProjects: [],
      fetchingAllProjects: false,
      replacingDesignForProjects: [],
      fetchingProjectsForDoc: [],
      fetchingProjectsForProduct: [],
      syncingProductProjects: [],
      syncingDocumentProjects: []
    });
    this.bindMediatorEvents();
  }

  bindMediatorEvents() {
    this.mediator.on(config.events.designCenter.fetchProjects, this.fetchAllProjects, this);
    this.mediator.on(config.events.designCenter.fetchProjectsForDocument, this.fetchProjectsForDocument, this);
    this.mediator.on(config.events.designCenter.fetchProjectsForProduct, this.fetchProjectsForProduct, this);
    this.mediator.on(config.events.designCenter.createProject, this.createNewProject, this);
    this.mediator.on(config.events.designCenter.copyProject, this.copyProject, this);
    this.mediator.on(config.events.designCenter.deleteProject, this.deleteProject, this);
    // save to project events
    this.mediator.on(config.events.designCenter.syncDocumentProjects, this.syncDocumentProjects, this);
    this.mediator.on(config.events.designCenter.syncProductProjects, this.syncProductProjects, this);
    //envision design events
    this.mediator.on(config.events.designCenter.replaceDesign, this.replaceDesign, this);
    this.mediator.on(config.events.designCenter.changeProjectName, this.changeName, this);
  }

  isCreatingProject(projectName) {
    return _.contains(this.model.get('creatingProjects'), projectName);
  }

  isDeletingProject(projectId) {
    return _.contains(this.model.get('deletingProjects'), projectId);
  }

  isFetchingDocumentProjects(documentUri) {
    return _.contains(this.model.get('fetchingProjectsForDoc'), documentUri);
  }

  isFetchingProductProjects(productId) {
    return _.contains(this.model.get('fetchingProjectsForProduct'), productId);
  }

  isSyncingDocumentProjects(documentUri) {
    return _.contains(this.model.get('syncingDocumentProjects'), documentUri);
  }

  isSyncingProductProjects(productId) {
    return _.contains(this.model.get('syncingProductProjects'), productId);
  }

  isReplacingDesign(projectId) {
    return _.contains(this.model.get('replacingDesignForProjects'), projectId);
  }

  getDocumentProjectsCollection(documentUri) {
    var entry = _.findWhere(this.documentUriProjectsCollections, { documentUri: documentUri });
    if (!entry) {
      entry = {
        documentUri: documentUri,
        collection: new ProjectsCollection()
      };
      this.documentUriProjectsCollections.push(entry);
    }
    return entry.collection;
  }

  getProductProjectsCollection(productId) {
    var entry = _.findWhere(this.productProjectsCollections, { projectId: productId });
    if (!entry) {
      entry = {
        projectId: productId,
        collection: new ProjectsCollection()
      };
      this.productProjectsCollections.push(entry);
    }
    return entry.collection;
  }

  fetchAllProjects() {
    this.model.set({ fetchingAllProjects: true });
    this.collection.fetch({
      data: utilities.addCustIpToDataObject({}),
      success: _.bind(function() {
        this.model.set({ fetchingAllProjects: false });
        this.mediator.trigger(config.events.designCenter.projectsFetchedFromServer);
      }, this),
      error: _.bind(function(models, response) {
        this.checkResponseStatus(response.status);
        this.model.set({ fetchingAllProjects: false });
      }, this)
    });
  }

  fetchProjectsForDocument(documentUri) {
    var docProjectsCollection = this.getDocumentProjectsCollection(documentUri);
    this.includeInFetchingForDocsList(documentUri);
    docProjectsCollection.fetch({
      data: utilities.addCustIpToDataObject({ forDocument: documentUri }),
      success: _.bind(function() {
        this.includeInFetchingForDocsList(documentUri, false);
        this.mediator.trigger(config.events.designCenter.projectsFetchedForDocument, docProjectsCollection);
      }, this),
      error: _.bind(function(models, response) {
        this.includeInFetchingForDocsList(documentUri, false);
        this.checkResponseStatus(response.status);
        var err = ServerErrorResponseModel.createFromXHR(response);
        this.mediator.trigger(config.events.designCenter.fetchProjectsForDocumentFailed, err);
      }, this)
    });
  }

  fetchProjectsForProduct(productId) {
    var productProjectsCollection = this.getProductProjectsCollection(productId);
    this.includeInFetchingForProductsList(productId);
    productProjectsCollection.fetch({
      data: utilities.addCustIpToDataObject({ forProduct: productId }),
      success: _.bind(function() {
        this.includeInFetchingForProductsList(productId, false);
        this.mediator.trigger(config.events.designCenter.projectsFetchedForProduct, productProjectsCollection);
      }, this),
      error: _.bind(function(models, response) {
        this.includeInFetchingForProductsList(productId, false);
        this.checkResponseStatus(response.status);
        var err = ServerErrorResponseModel.createFromXHR(response);
        this.mediator.trigger(config.events.designCenter.fetchProjectsForProductFailed, err);
      }, this)
    });
  }

  createNewProject(projectName) {
    if (!this.isCreatingProject(projectName)) {
      this.includeInCreatingProjectsList(projectName);
      this.collection.create({ name: projectName }, {
        wait: true,
        handleErrorLocally: true,
        success: _.bind(function(model) {
          this.includeInCreatingProjectsList(projectName, false);
          this.mediator.trigger(config.events.designCenter.projectCreatedOnServer, model);
        }, this),
        error: _.bind(function(model, response) {
          this.checkResponseStatus(response.status);
          this.includeInCreatingProjectsList(projectName, false);
          var err = ServerErrorResponseModel.createFromXHR(response);
          this.mediator.trigger(config.events.designCenter.createProjectFailed, projectName, err);
        }, this)
      });
    }
  }

  copyProject(projectName, projectId) {
    if (!this.isCreatingProject(projectName)) {
      this.includeInCreatingProjectsList(projectName);
      this.collection.create({ projectId: projectId, name: projectName }, {
        url: config.urls.projects.api + '/copy',
        wait: true,
        handleErrorLocally: true,
        success: _.bind(function(model) {
          this.includeInCreatingProjectsList(projectName, false);
          this.mediator.trigger(config.events.designCenter.projectCreatedOnServer, model);
        }, this),
        error: _.bind(function(model, response) {
          this.checkResponseStatus(response.status);
          this.includeInCreatingProjectsList(projectName, false);
          var err = ServerErrorResponseModel.createFromXHR(response);
          this.mediator.trigger(config.events.designCenter.deleteProjectFailed, projectName, err);
        }, this)
      });
    }
  }

  deleteProject(projectId) {
    if (!this.isDeletingProject(projectId)) {
      this.includeInDeletingProjectsList(projectId);
      var model = this.collection.get(projectId) || new ProjectModel({ id: projectId });
      model.destroy({
        wait: true,
        handleErrorLocally: true,
        success: _.bind(function(model) {
          this.includeInDeletingProjectsList(projectId, false);
          this.mediator.trigger(config.events.designCenter.projectDeletedOnServer, model);
        }, this),
        error: _.bind(function(model, response) {
          this.checkResponseStatus(response.status);
          this.includeInDeletingProjectsList(projectId, false);
          var err = ServerErrorResponseModel.createFromXHR(response);
          this.mediator.trigger(config.events.designCenter.deleteProjectFailed, projectId, err);
        }, this)
      });
    }
  }

  includeInCreatingProjectsList(projectName, include) {
    this.includeInList('creatingProjects', projectName, include);
  }

  includeInDeletingProjectsList(projectId, include) {
    this.includeInList('deletingProjects', projectId, include);
  }

  includeInFetchingForDocsList(documentUri, include) {
    this.includeInList('fetchingProjectsForDoc', documentUri, include);
  }

  includeInFetchingForProductsList(productId, include) {
    this.includeInList('fetchingProjectsForProduct', productId, include);
  }

  addLinkedProjectsToPostData(postData, linkProjectsCollection) {
    linkProjectsCollection.each(function(linkProject) {
      postData.updates[linkProject.get('id')] = linkProject.get('state');
    });
    return postData;
  }

  syncDocumentProjects(documentUri, linkProjectsCollection) {
    this.includeInList('syncingDocumentProjects', documentUri, true);
    var postData = { documentUri: documentUri, updates: {} };
    this.addLinkedProjectsToPostData(postData, linkProjectsCollection);
    $.ajax({
      url: config.urls.projects.documentsApi,
      handleErrorLocally: true,
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
      method: 'POST',
      data: JSON.stringify(utilities.addCustIpToDataObject(postData)),
      success: _.bind(function() {
        this.includeInList('syncingDocumentProjects', documentUri, false);
        this.mediator.trigger(config.events.designCenter.documentProjectsSynced, documentUri, linkProjectsCollection);
      }, this),
      error: _.bind(function(response) {
        this.includeInList('syncingDocumentProjects', documentUri, false);
        this.checkResponseStatus(response.status);
        var err = ServerErrorResponseModel.createFromXHR(response);
        this.mediator.trigger(config.events.designCenter.documentProjectsSyncFailed, documentUri, err);
      }, this)
    });
  }

  syncProductProjects(productId, linkProjectsCollection) {
    this.includeInList('syncingProductProjects', productId, true);
    var postData = { productId: productId, updates: {} };
    this.addLinkedProjectsToPostData(postData, linkProjectsCollection);
    $.ajax({
      url: config.urls.projects.productsApi,
      handleErrorLocally: true,
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
      method: 'POST',
      data: JSON.stringify(utilities.addCustIpToDataObject(postData)),
      success: _.bind(function() {
        this.includeInList('syncingProductProjects', productId, false);
        this.mediator.trigger(config.events.designCenter.productProjectsSynced, productId, linkProjectsCollection);
      }, this),
      error: _.bind(function(response) {
        this.includeInList('syncingProductProjects', productId, false);
        this.checkResponseStatus(response.status);
        var err = ServerErrorResponseModel.createFromXHR(response);
        this.mediator.trigger(config.events.designCenter.productProjectsSyncFailed, productId, err);
      }, this)
    });
  }

  replaceDesign(projectId, pubId) {
    var postData = { pubId: pubId };
    this.includeInList('replacingDesignForProjects', projectId, true);
    $.ajax({
      url: config.urls.projects.api + '/' + projectId + '/designs',
      handleErrorLocally: true,
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
      method: 'POST',
      data: JSON.stringify(utilities.addCustIpToDataObject(postData)),
      success: _.bind(function(data) {
        this.includeInList('replacingDesignForProjects', projectId, false);
        this.mediator.trigger(config.events.designCenter.projectDesignReplaced, projectId, pubId, data);
      }, this),
      error: _.bind(function(response) {
        this.includeInList('replacingDesignForProjects', projectId, false);
        this.checkResponseStatus(response.status);
        var err = ServerErrorResponseModel.createFromXHR(response);
        this.mediator.trigger(config.events.designCenter.replaceDesignFailed, projectId, pubId, err);
      }, this)
    });
  }

  changeName(projectId, val) {

    var postData = {
      name: val
    };

    $.ajax({
      url: config.urls.projects.api + '/' + projectId,
      handleErrorLocally: true,
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
      method: 'PUT',
      data: JSON.stringify(utilities.addCustIpToDataObject(postData)),
      success: _.bind(function() {
        //we're going to work on a success response
      }, this),
      error: _.bind(function() {
        this.mediator.trigger(events.error.generic);
      }, this)
    });

  }

}




// WEBPACK FOOTER //
// ./assets/services/ProjectsService.js